/**
 * Schedule Types (Location-level content scheduling)
 * Web version - Same types as mobile for consistency
 */

/**
 * Scheduling mode enum
 */
export enum SchedulingMode {
  FREQUENCY = 'Frequency',
  SPECIFIC_TIMES = 'SpecificTimes',
}

/**
 * Base scheduling rule properties
 */
export interface SchedulingRuleBase {
  id: string;
  schedulingMode: SchedulingMode;
  activeFrom: string | null; // TimeOnly "HH:MM"
  activeUntil: string | null; // TimeOnly "HH:MM"
  badge: string; // Human-readable summary (e.g., "3x/hr · 09:00-18:00")
  createdAt: string;
  updatedAt?: string;
}

/**
 * Frequency-based scheduling rule
 */
export interface FrequencySchedulingRule extends SchedulingRuleBase {
  schedulingMode: SchedulingMode.FREQUENCY;
  frequency: number; // 1-12 times per hour
  specificTimes?: never;
}

/**
 * Specific times scheduling rule
 */
export interface SpecificTimesSchedulingRule extends SchedulingRuleBase {
  schedulingMode: SchedulingMode.SPECIFIC_TIMES;
  specificTimes: string[]; // Array of "HH:MM" times
  frequency?: never;
}

/**
 * Union of all scheduling rule types
 */
export type SchedulingRule = FrequencySchedulingRule | SpecificTimesSchedulingRule;

/**
 * Base schedule item properties
 */
export interface ScheduleItemBase {
  id: string;
  position: number;
  rules: SchedulingRule[];
  createdAt: string;
  updatedAt?: string;
}

/**
 * Playlist schedule item (advertisement or music)
 */
export interface PlaylistScheduleItem extends ScheduleItemBase {
  type: 'playlist';
  playlistId: string;
  playlistName: string;
  playlistType: 'Music' | 'Advertisement'; // Enum value from backend
}

/**
 * Clip schedule item (individual content)
 */
export interface ClipScheduleItem extends ScheduleItemBase {
  type: 'clip';
  contentId: string;
  contentName: string;
}

/**
 * Union of all schedule item types
 */
export type ScheduleItem = PlaylistScheduleItem | ClipScheduleItem;

/**
 * Complete location schedule DTO
 */
export interface ScheduleDto {
  id: string;
  locationId: string;
  items: ScheduleItem[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Request: Add a new schedule item
 */
export interface AddScheduleItemRequest {
  type: 'playlist' | 'clip';
  playlistId?: string;
  contentId?: string;
}

/**
 * Request: Create a scheduling rule
 */
export interface CreateSchedulingRuleRequest {
  schedulingMode: SchedulingMode;
  frequency?: number;
  specificTimes?: string[];
  activeFrom?: string | null;
  activeUntil?: string | null;
}

/**
 * Request: Update a scheduling rule
 */
export type UpdateSchedulingRuleRequest = CreateSchedulingRuleRequest;

/**
 * Request: Reorder schedule items
 */
export interface ReorderItemsRequest {
  itemIds: string[];
}

/**
 * Type guard: Check if rule is frequency-based
 */
export function isFrequencyRule(rule: SchedulingRule): rule is FrequencySchedulingRule {
  return rule.schedulingMode === SchedulingMode.FREQUENCY;
}

/**
 * Type guard: Check if rule is specific times
 */
export function isSpecificTimesRule(rule: SchedulingRule): rule is SpecificTimesSchedulingRule {
  return rule.schedulingMode === SchedulingMode.SPECIFIC_TIMES;
}

/**
 * Type guard: Check if item is playlist
 */
export function isPlaylistItem(item: ScheduleItem): item is PlaylistScheduleItem {
  return item.type === 'playlist';
}

/**
 * Type guard: Check if item is clip
 */
export function isClipItem(item: ScheduleItem): item is ClipScheduleItem {
  return item.type === 'clip';
}

/**
 * Schedule Preview Block (24-hour timeline visualization)
 */
export interface SchedulePreviewBlockDto {
  id: string;
  type: 'background' | 'scheduled';
  startTime: number; // minutes since midnight (0-1440)
  endTime: number; // minutes since midnight (0-1440)
  label: string;
  sublabel: string | null;
  ruleLabel: string | null;
  color: string; // CSS-like color
  isActive: boolean;
  sourceId: string;
  sourceType: 'timeblock' | 'scheduleItem' | 'scenario';
  intervalMinutes?: number; // for frequency rules
}

/**
 * Full 24-hour schedule preview DTO (for TimelinePreview component)
 */
export interface SchedulePreviewDto {
  locationId: string;
  date: string;
  openTimeMinutes: number; // minutes since midnight
  closeTimeMinutes: number; // minutes since midnight
  scenarioId: string | null;
  scenarioName: string | null;
  blocks: SchedulePreviewBlockDto[];
}
